import {
  type DropDownResult,
  isCheckboxChangeEventType,
  isDropDownResult,
  type JsonDataWrapper,
  type OdinDataRetrievalOptions,
  OdinDataSender,
} from '@myosh/odin-components';
import type { LocaleItem } from '../../../@types/common';
import type { CombinedFieldType, ExtendedDynamicFormField } from '../../../@types/forms';
import { riskMatrixApi } from '../../../redux/services/risk-matrix';
import type {
  FormBuilderField,
  FormBuilderFormGroup,
  FormBuilderFormSettings,
} from '../../../redux/slices/form-builder-slice';
import { forceAssert } from '../../../utils/type-guards';
import { getAIFieldData, getAiFieldOptions } from '../fields/ai-field/ai-field-form.util';
import { getDocFieldOptions, getEmailThisButtonData } from '../fields/email-this-button/email-this-button.util';
import { getLabelFieldValues } from '../fields/label/label-field-form.util';
import { getLinkFieldValues, targetOptions } from '../fields/link/link-field-form.util';
import { getTextAreaValues } from '../fields/textarea/textarea-field-form.util';
import {
  displayScoresOptions,
  getTextualQuestionnaireData,
} from '../fields/textual-questionnaire-field/textual-questionnaire-field-form.utils';
import { createTranslations } from '../form-builder-utils';
import { fieldTypeDropdownOptions } from './field-details.util';

import { appDispatch } from '../../../redux/store-accessors';
import { getRiskMatrixValues, riskRatingValueDisplayOptions } from '../fields/risk-rating/risk-rating-field-form.util';
import { getTimeFieldValues } from '../fields/timefield/time-field-form.util';

export const getFieldSpecificData = async (
  field: FormBuilderField,
  fieldGroups: FormBuilderFormGroup[],
  allFormFields?: ExtendedDynamicFormField[]
) => {
  switch (field.fieldType as CombinedFieldType) {
    case 'AI_FIELD':
      return getAIFieldData(field, allFormFields);
    case 'EMAIL_THIS_BUTTON':
      return getEmailThisButtonData(field, fieldGroups);
    case 'LABEL':
      return getLabelFieldValues(field);
    case 'LINK':
      return getLinkFieldValues(field);
    case 'TEXTAREA':
      return getTextAreaValues(field);
    case 'TEXTUAL_QUESTIONNAIRE':
      return getTextualQuestionnaireData(forceAssert(field));
    case 'TIMEFIELD':
      return getTimeFieldValues(field);
    case 'RISKRATING':
      return getRiskMatrixValues(field);
    default:
      return {};
  }
};

const translationValue = (value: unknown, localeIds: LocaleItem[]) => ({
  translations: createTranslations(String(value ?? ''), localeIds),
});

export const getFieldValue = (fieldName: string, fieldValue: unknown, localeIds: LocaleItem[]) => {
  let value: unknown = fieldValue;
  switch (fieldName) {
    case 'displayDescription':
    case 'showFieldCaption': {
      if (value && isCheckboxChangeEventType(value)) {
        value = value.checked;
      } else {
        value = fieldValue;
      }
      break;
    }
    case 'fieldType':
    case 'generateDocFieldId':
    case 'target':
    case 'riskMatrixId':
    case 'displayScores':
      value = isDropDownResult(fieldValue) ? fieldValue.value : '';
      break;
    case 'fieldCaption':
    case 'fieldData':
    case 'help':
    case 'hint':
    case 'buttonCaption':
      value = translationValue(fieldValue, localeIds);
      break;
    case 'aiInputFieldIds':
    case 'aiOutputFieldIds':
    case 'riskRatingValueDisplay':
      if (Array.isArray(fieldValue)) {
        value = fieldValue.map((item) => item.value);
      }
      break;
    case 'fieldGroups':
      if (Array.isArray(fieldValue)) {
        value = (fieldValue as DropDownResult[]).map((item, index) => ({
          id: item.value,
          caption: {
            translations: createTranslations(item.text, localeIds),
          },
          position: index,
        }));
      }
      break;
    case 'questions':
      if (Array.isArray(fieldValue)) {
        value = fieldValue.map(({ originalData: _originalData, ...item }) => ({
          ...item,
          id: item.id ?? null,
          caption: {
            translations: createTranslations(item.text, localeIds),
          },
        }));
      }
      break;
    case 'numberOfLines':
    default:
      value = fieldValue;
  }
  return value;
};

export const getFormBuilderFieldOptionsData = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  fieldGroups: FormBuilderFormGroup[],
  formData: FormBuilderFormSettings
) => {
  switch (options?.fieldId?.toString()) {
    case 'fieldType':
      subscriber.sendData({ data: fieldTypeDropdownOptions });
      break;
    case 'generateDocFieldId': {
      const generateDocFieldOptions = getDocFieldOptions(fieldGroups);
      subscriber.sendData({
        data: generateDocFieldOptions,
      });
      break;
    }
    case 'aiOutputFieldIds':
    case 'aiInputFieldIds': {
      const inputOutputFormFields = getAiFieldOptions(formData);
      subscriber.sendData({ data: inputOutputFormFields });
      break;
    }
    case 'target': {
      if (options.page === 1) {
        subscriber.sendData({
          data: targetOptions,
        });
      } else {
        subscriber.sendData();
      }
      break;
    }
    case 'displayScores': {
      if (options.page === 1) {
        subscriber.sendData({
          data: displayScoresOptions,
        });
      } else {
        subscriber.sendData();
      }
      break;
    }
    case 'riskRatingValueDisplay': {
      if (options.page === 1) {
        subscriber.sendData({ data: riskRatingValueDisplayOptions });
      } else {
        subscriber.sendData();
      }
      break;
    }
    case 'riskMatrixId': {
      try {
        const result = await appDispatch(
          riskMatrixApi.endpoints.getRiskMatrices.initiate({
            page: options.page || 1,
            pageSize: options.pageSize || 100,
          })
        );

        if (result.data) {
          const mappedData = result.data.map((matrix) => ({
            ...matrix,
            text: matrix.caption,
            value: matrix.id,
          }));
          subscriber.sendData({ data: mappedData, requestId: options.requestId });
        } else {
          subscriber.sendData();
        }
      } catch (error) {
        subscriber.sendData();
      }
      break;
    }
    default:
      subscriber.sendData();
      break;
  }
};
